import React, { useEffect, useState } from 'react'
import { Modal } from 'antd'
import { ArrowDownToLine, Building2, CalendarCheck, FileText, GitBranch, ListOrdered, Loader2, RefreshCw, Users, Wrench, X } from 'lucide-react'
import { orderReportOne } from '@/service/ordersService'
import ReportMarkdown from '../yhai/inventoryAnalysis/ReportMarkdown'
import ReportMindmap from '../yhai/inventoryAnalysis/ReportMindmap'
import RepairReportFlow from './RepairReportFlow'
import '../yhai/inventoryAnalysis/style.less'
import './viewReport.less'

function ReportState({ loading, error, onRetry }) {
    return (
        <div className="inventory-state" role={loading ? 'status' : error ? 'alert' : undefined}>
            {loading ? <Loader2 size={28} className="inventory-spinning" /> : <FileText size={32} strokeWidth={1.4} />}
            <h3>{loading ? '正在读取维修报告' : error ? '维修报告暂时无法读取' : '该工单尚未生成维修报告'}</h3>
            {!loading && (
                <>
                    <p>{error ? '请确认登录与工单查看权限，或稍后重试。' : '生成完成后可在这里查看报告正文、思维导图和工单流程。'}</p>
                    <button className="inventory-button" onClick={onRetry}>
                        <RefreshCw size={15} />
                        重新加载
                    </button>
                </>
            )}
        </div>
    )
}

function downloadReport(report, orderId) {
    const info = report.reportInfo && report.reportInfo.orderInfo
    const name = `${info && info.orderCode ? info.orderCode : orderId}-维修分析报告`.replace(/[\\/:*?"<>|]/g, '-')
    const url = URL.createObjectURL(new Blob([report.report], { type: 'text/markdown;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `${name}.md`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function RepairReportReader({ orderId }) {
    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [refresh, setRefresh] = useState(0)
    const [view, setView] = useState(() => (window.matchMedia('(max-width: 600px)').matches ? 'markdown' : 'mindmap'))
    useEffect(() => {
        let active = true
        setLoading(true)
        setError(false)
        // 切换工单或关闭弹层后丢弃迟到响应，不把上一张工单的内容带到新工单。
        orderReportOne(orderId)
            .then((response) => {
                if (!response || response.retCode !== 200) throw new Error('报告读取失败')
                if (active) setReport(response.retData)
            })
            .catch(() => {
                if (active) setError(true)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [orderId, refresh])

    const retry = () => setRefresh((value) => value + 1)
    const hasReport = report && typeof report.report === 'string' && report.report.trim()
    if (loading || error || !hasReport) return <ReportState loading={loading} error={error} onRetry={retry} />
    const info = (report.reportInfo && report.reportInfo.orderInfo) || {}
    const facts = [
        ['工单编号', info.orderCode || orderId, FileText, 'blue'],
        ['所属机构', info.organName, Building2, 'green'],
        ['维修团队', info.teamName, Users, 'orange'],
        ['完成时间', info.orderCompletedTime && info.orderCompletedTime.replace('T', ' '), CalendarCheck, 'indigo'],
    ]
    return (
        <>
            <header className="repair-report-heading">
                <div className="repair-report-title">
                    <span className="repair-report-emblem">
                        <Wrench size={24} strokeWidth={1.7} />
                    </span>
                    <div>
                        <p>工单维修报告</p>
                        <h2>{info.customerVehicleCode || info.orderCode || '维修分析报告'}</h2>
                    </div>
                </div>
                <button className="inventory-button" onClick={retry}>
                    <RefreshCw size={15} />
                    刷新报告
                </button>
            </header>
            <dl className="repair-report-facts" aria-label="工单基本信息">
                {facts.map(([label, value, Icon, tone]) => (
                    <div key={label} data-tone={tone}>
                        <dt>
                            <Icon size={16} aria-hidden="true" />
                            {label}
                        </dt>
                        <dd>{value || '未记录'}</dd>
                    </div>
                ))}
            </dl>
            <div className="inventory-reading-bar">
                <nav aria-label="维修报告展示方式">
                    {[
                        ['mindmap', '思维导图', GitBranch],
                        ['markdown', '报告正文', FileText],
                        ['flow', '流程分析', ListOrdered],
                    ].map(([key, label, Icon]) => (
                        <button key={key} aria-pressed={view === key} className={view === key ? 'active' : ''} onClick={() => setView(key)}>
                            <Icon size={16} />
                            {label}
                        </button>
                    ))}
                </nav>
                <button className="inventory-button" onClick={() => downloadReport(report, orderId)}>
                    <ArrowDownToLine size={15} />
                    下载报告
                </button>
            </div>
            <div className="repair-report-content">
                {view === 'mindmap' && <ReportMindmap markdown={report.report} reportType="repair" />}
                {view === 'markdown' && <ReportMarkdown markdown={report.report} />}
                {view === 'flow' && <RepairReportFlow orderInfo={report.reportInfo && report.reportInfo.orderInfo} />}
            </div>
        </>
    )
}

export default function ViewReport({ orderId, visible, setViewReportInfo }) {
    return (
        <Modal
            visible={visible}
            title="维修分析报告"
            width="min(1440px, calc(100vw - 48px))"
            footer={null}
            className="repair-report-modal"
            closeIcon={<X size={20} />}
            destroyOnClose
            onCancel={() => setViewReportInfo({ visible: false, orderId: '' })}
        >
            <section className="inventory-report-page repair-report" aria-label="工单维修分析报告">
                {visible && orderId && <RepairReportReader key={orderId} orderId={orderId} />}
            </section>
        </Modal>
    )
}
